import { useState, useEffect } from 'react';
import type { User } from '../schemas/validation';
import { adminApi } from '../api/client';
import { clearAllRoarySessionStorage } from './useRoary';

interface MimickedUser {
  id: string;
  name: string;
  email: string;
}

interface AuthState {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isMimicking: boolean;
  mimickedUser: MimickedUser | null;
  originalToken: string | null;
}

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    loading: true,
    isAuthenticated: false,
    isAdmin: false,
    isMimicking: false,
    mimickedUser: null,
    originalToken: null,
  });

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setAuthState({
          user: null,
          loading: false,
          isAuthenticated: false,
          isAdmin: false,
          isMimicking: false,
          mimickedUser: null,
          originalToken: null,
        });
        return;
      }

      // Check if we're in mimic mode
      const originalToken = localStorage.getItem('originalAdminToken');
      const mimickedUserData = localStorage.getItem('mimickedUser');
      const isMimicking = !!originalToken && !!mimickedUserData;

      // Get current user profile
      const user = await adminApi.getCurrentUser();
      setAuthState({
        user,
        loading: false,
        isAuthenticated: true,
        isAdmin: isMimicking ? false : user.isAdmin, // When mimicking, don't treat as admin
        isMimicking,
        mimickedUser: isMimicking ? JSON.parse(mimickedUserData!) : null,
        originalToken: originalToken,
      });
    } catch {
      // Token is invalid or expired
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      localStorage.removeItem('originalAdminToken');
      localStorage.removeItem('mimickedUser');
      setAuthState({
        user: null,
        loading: false,
        isAuthenticated: false,
        isAdmin: false,
        isMimicking: false,
        mimickedUser: null,
        originalToken: null,
      });
    }
  };

  const updateUser = (user: User) => {
    setAuthState(prev => ({
      ...prev,
      user,
      isAdmin: prev.isMimicking ? false : user.isAdmin,
    }));
  };

  const startMimicSession = async (userId: string) => {
    try {
      // Save the current admin token
      const currentToken = localStorage.getItem('authToken');
      if (!currentToken) {
        throw new Error('No authentication token found');
      }

      // Call API to get mimic token
      const response = await adminApi.startMimicSession(userId);

      // Store original token and mimic data
      localStorage.setItem('originalAdminToken', currentToken);
      localStorage.setItem('mimickedUser', JSON.stringify(response.mimickedUser));
      localStorage.setItem('authToken', response.token);

      // Update state
      setAuthState(prev => ({
        ...prev,
        isMimicking: true,
        mimickedUser: response.mimickedUser,
        originalToken: currentToken,
        isAdmin: false, // While mimicking, don't treat as admin
      }));

      // Refresh to get the mimicked user's data
      window.location.reload();
    } catch (error) {
      console.error('Failed to start mimic session:', error);
      throw error;
    }
  };

  const exitMimicSession = () => {
    const originalToken = localStorage.getItem('originalAdminToken');
    if (originalToken) {
      // Restore original admin token
      localStorage.setItem('authToken', originalToken);
      localStorage.removeItem('originalAdminToken');
      localStorage.removeItem('mimickedUser');

      // Refresh to restore admin view
      window.location.reload();
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('originalAdminToken');
    localStorage.removeItem('mimickedUser');
    // Wipe every Roary chat cache in this browser — signing out must never
    // leave the next signed-in user able to hydrate a previous chat.
    clearAllRoarySessionStorage();
    setAuthState({
      user: null,
      loading: false,
      isAuthenticated: false,
      isAdmin: false,
      isMimicking: false,
      mimickedUser: null,
      originalToken: null,
    });
  };

  useEffect(() => {
    checkAuth();
  }, []);

  return {
    ...authState,
    checkAuth,
    updateUser,
    logout,
    startMimicSession,
    exitMimicSession,
  };
};
